import React, { FunctionComponent, useContext } from "react";
import { Link } from "react-router-dom";
import { useFlags } from "launchdarkly-react-client-sdk";
import { FormattedMessage } from "react-intl-phraseapp";
import { Beneficiary, PlanDetailsSection } from "../@types";
import {
  formatDate,
  makePlanDisplayId,
  makePlanLongId,
  getLowIncomeSubsidyStatusLabel,
  useTranslate,
} from "../helpers";
import routes from "../app/routes";
import { AppStore } from "../app/store";
import { PlanDetailsHashLink } from ".";

const BeneficiaryInfo: FunctionComponent<{
  bene: Beneficiary;
}> = ({ bene }) => {
  const t = useTranslate();
  const { mctCurrentPlanYear } = useFlags();
  const {
    state: { year, language, nextYearCoverage },
  } = useContext(AppStore.AppContext);

  const forNextYear = year !== mctCurrentPlanYear?.toString();
  const extraHelpLabelKey = forNextYear
    ? "bene_landing.next_year_extra_help"
    : "bene_landing.current_extra_help";

  let currentCoverageElem: React.ReactFragment;
  let testId = "";

  if (bene.current_coverage.length) {
    testId = "current-coverage-name";
    currentCoverageElem = (
      <>
        <span className="ds-u-font-weight--bold">
          {t("app.terms.current_coverage")}
        </span>{" "}
        {bene.current_coverage[0].name}{" "}
        {makePlanDisplayId(bene.current_coverage[0])}{" "}
        <Link
          to={`${routes.planDetails.replace(
            ":id",
            makePlanLongId(bene.current_coverage[0])
          )}?lang=${language}&year=${bene.current_coverage[0].coverage_year}`}
        >
          {t("bene_landing.view_plan_details")}
        </Link>
      </>
    );
  } else {
    testId = "current-coverage-none";
    currentCoverageElem = (
      <>
        <span className="ds-u-font-weight--bold">
          {t("app.terms.current_coverage")}
        </span>{" "}
        {t("none")}
      </>
    );
  }

  let potentialNextCoverageElem: React.ReactFragment = "";

  if (nextYearCoverage && forNextYear) {
    testId = "potential-next-coverage";
    potentialNextCoverageElem = (
      <>
        <span className="ds-u-font-weight--bold">
          <FormattedMessage
            id="bene_landing.coverage_with_no_change"
            values={{ year: mctCurrentPlanYear + 1 }}
          />
        </span>{" "}
        {nextYearCoverage.name} {makePlanDisplayId(nextYearCoverage)}{" "}
        <Link
          to={`${routes.planDetails.replace(
            ":id",
            makePlanLongId(nextYearCoverage)
          )}?lang=${language}&year=${nextYearCoverage.contract_year}`}
        >
          {t("bene_landing.view_plan_details")}
        </Link>
      </>
    );
  }

  let futureCoverageNameElem: React.ReactFragment = "";
  let futureCoverageStartOrConfirmationElem: React.ReactFragment = "";

  if (bene.future_coverage.length) {
    futureCoverageNameElem = (
      <>
        <span className="ds-u-font-weight--bold">
          {t("bene_landing.new_coverage")}:
        </span>{" "}
        {bene.future_coverage[0].name || ""}{" "}
        {makePlanDisplayId(bene.future_coverage[0])}
      </>
    );
    futureCoverageStartOrConfirmationElem = (
      <>
        <span className="ds-u-font-weight--bold">
          {t("bene_landing.coverage_starts")}:
        </span>{" "}
        {formatDate(
          bene.future_coverage[0].enrollment_effective_date,
          "M/d/yyyy"
        )}
      </>
    );
  } else if (bene.oec_status) {
    futureCoverageNameElem = (
      <>
        <span className="ds-u-font-weight--bold">
          {t("bene_landing.requested_to_join")}:
        </span>{" "}
        {bene.oec_status.plan_name} {makePlanDisplayId(bene.oec_status)}
      </>
    );
    futureCoverageStartOrConfirmationElem = (
      <>
        <span className="ds-u-font-weight--bold">
          {t("future_enrollment.confirmation_number")}:
        </span>{" "}
        {bene.oec_status.confirmation_number}{" "}
        <PlanDetailsHashLink
          label={t("bene_landing.contact_plan_for_processing_details")}
          plan={{
            ...bene.oec_status,
            contract_year: bene.oec_status.year,
          }}
          section={PlanDetailsSection.CONTACT_INFO}
        />
      </>
    );
  }

  return (
    <>
      <ul className="ds-c-list--bare ds-u-margin-y--1" role="list">
        <li className="ds-u-margin-y--1" data-testid={testId} role="listitem">
          {potentialNextCoverageElem || currentCoverageElem}
        </li>
        <li className="ds-u-margin-y--1" role="listitem">
          <span
            className="ds-u-font-weight--bold"
            data-testid={extraHelpLabelKey}
          >
            <FormattedMessage id={extraHelpLabelKey} values={{ year }} />:
          </span>{" "}
          {year &&
            t(
              getLowIncomeSubsidyStatusLabel(
                year,
                forNextYear ? bene.future_lis_level : bene.lis_level
              )
            )}
        </li>
        {futureCoverageNameElem && (
          <>
            <li aria-hidden className="ds-u-margin-y--2" role="listitem">
              <hr className="ds-u-border--1" />
            </li>
            <li
              className="ds-u-margin-y--1"
              data-testid="future-enrollment"
              role="listitem"
            >
              {futureCoverageNameElem}
            </li>
            <li
              className="ds-u-margin-y--1"
              data-testid="confirmation"
              role="listitem"
            >
              {futureCoverageStartOrConfirmationElem}
            </li>
          </>
        )}
      </ul>
    </>
  );
};

export default BeneficiaryInfo;
